import React, { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import IconWithLabel from 'web-library/stories/atom/IconWithLabel/IconWithLabel';
import {
  ApiButton,
  Avatar,
  Box,
  Button,
  ContentTemplate,
  Grid,
  Icon,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  SearchBar,
  SectionHeader,
  TableActionTypes,
  TableState,
  Typography,
} from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { rowsPerPageOptionsDefault } from '../../logic/useWithdrawalDataTable';
import NavigationButtons from '../../../../components/NavigationButtons';
import DraftTimeStamp from '../../../../components/DraftTimeStamp';
import {
  EntityModelList,
  GroupExamWithdrawalCandidate,
  GroupExamWithdrawalDeleteCandidateRequest,
} from '../../../../api/candidate/exam/model';
import EditButton from '../../../../components/buttons/EditButton';
import DeleteButton from '../../../../components/buttons/DeleteButton';
import RouteKey from '../../../../routes/key';

const useStyles = makeStyles()((theme) =>
({
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      paddingBottom: theme.spacing(1),
    },
  },
  selectCandidatesContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  buttonContainer: {
    marginLeft: theme.spacing(5),
    marginBottom: theme.spacing(6),
    display: 'inline-flex',
    cursor: 'pointer',
    '&:hover': {
      '& $icon, & $button': {
        color: theme.color.link.linkHover01,
      },
      '& $iconRemove, $buttonRemove': {
        color: theme.color.link.linkHover02,
      },
    },
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(0),
    },
  },
  avatarFilter: {
    '&.MuiAvatar-root.MuiAvatar-circular': {
      backgroundColor: theme.color.background.bgMain,
      border: 'none',
      borderRadius: '18px',
      padding: '6px 12px',
      marginLeft: theme.spacing(1),
      marginRight: '0',
      marginBottom: theme.spacing(6),
      '& span': {
        color: [theme.color.pill.pillInfoText, '!important'],
        fontSize: '14px',
      },
    },
  },
  button: {
    margin: 'auto',
    padding: 10,
    minWidth: 0,
  },
  icon: {
    margin: 'auto',
  },
  searchBar: {
    marginBottom: theme.spacing(5),
  },
  dataTable: {
    marginBottom: theme.spacing(7),
  },
  navigationButtonContainer: {
    marginBottom: theme.spacing(2),
  },
  errString: {
    color: theme.color.state.stateError,
    marginBottom: theme.spacing(1),
  },
}));

interface SelectCandidatesContentProps {
  setSelectedCandidateInfo: (selectedCandidateInfo: GroupExamWithdrawalCandidate) => void;
  setDraftFiles: (draftFiles: UploadedFile[]) => void;
  setIsUploadEditDocumentsModalOpen: (isUploadEditDocumentsModalOpen: boolean) => void;
  setSelectedCandidate: (selectedCandidate: GroupExamWithdrawalDeleteCandidateRequest) => void;
  setSelectedCandidateName: (selectedCandidateName: string) => void;
  setRemoveCandidateModal: (removeCandidateModal: boolean) => void;
  handleConfirmCandidates: () => void;
  documentError: boolean;
  onAddCandidatesDialogOpen: () => void;
  lastUpdatedTime?: string;
  state: TableState;
  tableDispatch: Dispatch<TableActionTypes>;
  tOptions: MUIDataTableOptions;
}

const getTableColumn = (
  t: any,
  state: TableState,
  onUploadDocumentsModalOpen: any,
  getDeleteButtonDef: any
): MUIDataTableColumnDef[] => [
    {
      name: 'NRIC/FIN/Passport No.',
      label: t('withdrawal->>Coordinator.SelectCandidates.NRIC'),
      options: {
        setCellProps: () => ({ style: { width: '18%' } }),
        sort: false,
        searchable: true,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Box>
            <Typography variant="body2">{state.data[dataIndex].candidateId}</Typography>
          </Box>
        ),
      },
    },
    {
      name: 'candidateName',
      label: t('withdrawal->>Coordinator.SelectCandidates.CandidateName'),
      options: {
        sort: false,
        searchable: true,
        filter: false,
        setCellProps: () => ({ style: { width: '18%' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Box>
            <Typography variant="body2">{state.data[dataIndex].name}</Typography>
          </Box>
        ),
      },
    },
    {
      name: 'examLevel',
      label: t('withdrawal->>Coordinator.SelectCandidates.ExamLevel'),
      options: {
        setCellProps: () => ({ style: { width: '15%' } }),
        sort: false,
        searchable: true,
        customBodyRenderLite: (dataIndex: number) => (
          <Box>
            {state.data[dataIndex].examLevel.map((exam: string, examIndex: number) => (
              <Typography key={exam} variant="body2">
                {exam}
                {examIndex < state.data[dataIndex].examLevel.length - 1 && <>,</>}
              </Typography>
            ))}
          </Box>
        ),
      },
    },
    {
      name: 'examSeries',
      label: t('withdrawal->>Coordinator.SelectCandidates.ExamSeries'),
      options: {
        setCellProps: () => ({ style: { width: '13%' } }),
        sort: false,
        searchable: true,
      },
    },
    {
      name: 'Supporting Document(s)',
      label: t('withdrawal->>Coordinator.SelectCandidates.SupportingDocuments'),
      options: {
        setCellProps: () => ({ style: { width: '20%' } }),
        sort: false,
        searchable: true,
        customBodyRenderLite: (dataIndex: number) => (
          <>
            {state.data[dataIndex].documents.length === 0 ? (
              <ApiButton
                id={`button-upload-document-${dataIndex}`}
                variant="text"
                color="primary"
                status="idle"
                size="small"
                style={{ padding: 0, minWidth: 0 }}
                onClick={() => onUploadDocumentsModalOpen(dataIndex)}
              >
                <IconWithLabel
                  iconName={IconName.download}
                  iconSize="M"
                  label={t('withdrawal->>Coordinator.SelectCandidates.UploadDocuments')}
                  typographyProps={{
                    variant: 'h5',
                    align: 'left',
                  }}
                />
              </ApiButton>
            ) : (
              <>
                <Box>
                  {state.data[dataIndex].documents.map(
                    (document: EntityModelList, documentIndex: number) => (
                      <Typography key={document.uuid} variant="body2">
                        {document.fileName}
                        {documentIndex < state.data[dataIndex].documents.length - 1 && <>,</>}
                      </Typography>
                    )
                  )}
                </Box>
                <EditButton
                  buttonId={dataIndex}
                  label={t('withdrawal->>Coordinator.SelectCandidates.EditDocuments')}
                  editHandler={() => onUploadDocumentsModalOpen(dataIndex)}
                />
              </>
            )}
          </>
        ),
      },
    },
    {
      name: 'Action(s)',
      label: t('withdrawal->>Coordinator.SelectCandidates.Actions'),
      options: {
        setCellProps: () => ({ style: { width: '16%' } }),
        sort: false,
        searchable: false,
        customBodyRenderLite: (dataIndex: number) => <>{getDeleteButtonDef(dataIndex)}</>,
      },
    },
  ];

const { withdrawal } = RouteKey.child;
const { withdrawal: withdrawalRoute, reason } = RouteKey.withdrawal.child;

const SelectCandidatesContent = ({
  setSelectedCandidateInfo,
  setDraftFiles,
  setIsUploadEditDocumentsModalOpen,
  setSelectedCandidate,
  setSelectedCandidateName,
  setRemoveCandidateModal,
  handleConfirmCandidates,
  documentError,
  onAddCandidatesDialogOpen,
  lastUpdatedTime,
  state,
  tableDispatch,
  tOptions,
}: SelectCandidatesContentProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const theme = useTheme();

  const history = useHistory();
  const { uuid: applicationUUID } = useParams<{ uuid: string }>();

  const onUploadDocumentsModalOpen = (dataIndex: number) => {
    setSelectedCandidateInfo(state.data[dataIndex]);

    const filesDraft = state.data[dataIndex].documents.map((draftFile: EntityModelList) => {
      const newFile = new File([new ArrayBuffer(draftFile.fileSize)], draftFile.fileName);
      return {
        file: newFile,
        hasError: false,
        pending: false,
        uploaded: true,
        key: draftFile.uuid,
      };
    });
    setDraftFiles(filesDraft);

    setIsUploadEditDocumentsModalOpen(true);
  };

  const getDeleteButtonDef = (dataIndex: number) => (
    <DeleteButton
      buttonId={`candidate-${dataIndex}`}
      label={t('withdrawal->>Coordinator.SelectCandidates.Remove')}
      deleteHandler={() => {
        setSelectedCandidate({
          applicationUuid: applicationUUID,
          candidateUuid: state.data[dataIndex].uuid,
        });
        setSelectedCandidateName(state.data[dataIndex].name);
        setRemoveCandidateModal(true);
      }}
    />
  );

  const tableColumns = getTableColumn(t, state, onUploadDocumentsModalOpen, getDeleteButtonDef);

  return (
    <ContentTemplate type="full">
      <Grid className={classes.headerContainer}>
        <Box className={classes.selectCandidatesContainer}>
          <SectionHeader title={t('withdrawal->>Coordinator.SelectCandidates.SelectCandidates')} />
          <Avatar className={classes.avatarFilter} type="text">
            {state.data.length}
          </Avatar>
        </Box>

        <Box className={classes.buttonContainer}>
          <Button
            id="button-accessArrangement-addRequest"
            className={classes.button}
            startIcon={
              <Icon
                size="M"
                className={classes.icon}
                name={IconName.add}
                color={theme.color.icon.iconSelected}
              />
            }
            onClick={onAddCandidatesDialogOpen}
            variant="text"
            color="primary"
          >
            {t('withdrawal->>Coordinator.SelectCandidates.AddCandidates')}
          </Button>
        </Box>
      </Grid>
      <Box className={classes.searchBar}>
        <SearchBar
          placeholder={t('withdrawal->>Coordinator.SelectCandidates.SearchSubjects')}
          name="searchInput"
          ariaLabel="searchInput"
          defaultValue={state.search}
          onSearch={(searchStr: string) => {
            tableDispatch({
              type: 'UPDATE_SEARCH',
              payload: searchStr,
            });
          }}
        />
      </Box>
      <Box className={classes.dataTable}>
        <MUIDataTable
          state={state}
          dispatch={tableDispatch}
          columns={tableColumns}
          data={state.data}
          emptyDataLabel={t('withdrawal->>Coordinator.SelectCandidates.EmptyDataTableContent')}
          options={{
            ...tOptions,
            sort: false,
            pagination: true,
          }}
          rowsPerPageOptions={rowsPerPageOptionsDefault}
        />
      </Box>
      <Box className={classes.navigationButtonContainer}>
        {documentError && (
          <Typography className={classes.errString} variant="body2">
            {t('withdrawal->>Coordinator.SelectCandidates.MissingUploadDocuments')}
          </Typography>
        )}
        <NavigationButtons
          backLabel={t('withdrawal->>Coordinator.SelectCandidates.Back')}
          onBackClick={() =>
            history.push(
              `/${withdrawal}/${withdrawalRoute.route}/${applicationUUID}/${reason.route}`
            )
          }
          onProceedClick={handleConfirmCandidates}
          procceedLabel={t('withdrawal->>Coordinator.SelectCandidates.ConfirmCandidates')}
          isDisabledProceedButton={state.data.length === 0}
        />
      </Box>
      {lastUpdatedTime && <DraftTimeStamp savedAt={lastUpdatedTime} />}
    </ContentTemplate>
  );
};

export default SelectCandidatesContent;
